import {
    Component,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ViewportScroller } from '@angular/common';

import { PostFooterComponent } from '../insertable/post-footer/post-footer.component';
import { ScrollPositionService } from '../../services/scroll-position.service';
import { UserService } from '../../services/user.service';
import { UserPostsDataService } from '../../services/posts.service';
import * as Classes from '../../classes/models.classes';

/**
 * Full dashboard UI preview for design — no AuthGuard on the route.
 * Loads the latest 10 posts for @truthshorts via the same API as the profile feed
 * so media carousels, score menus (Ratings Overview), and reactions match production.
 */
@Component({
    selector: 'app-preview-dashboard',
    templateUrl: './preview-dashboard.component.html',
    styleUrls: [
        './preview-dashboard.component.css',
        '../home/home.component.css',
    ],
})
export class PreviewDashboardComponent implements OnInit {
    /** Official account to mirror for live UI samples */
    readonly previewAccountHandle = 'truthshorts';

    UserID!: number;
    Username!: string;
    readonly avatarPlaceholder = 'assets/branding/avatar-placeholder.svg';

    readonly pageSize = 10;
    currentPage = 1;

    profileLoaded = false;
    profileError: string | null = null;
    postsLoading = false;
    postsError: string | null = null;

    UserPostsData: Classes.ShortsPosts[] = [];
    mediaUrls: any[] = [];
    hasPostText = false;
    replyTypes = [
        { id: 1, text: 'Everyone', icon: 'path/to/everyone.svg' },
        { id: 2, text: 'Accounts You Follow', icon: 'path/to/accounts-follow.svg' },
        { id: 3, text: 'Verified Accounts', icon: 'path/to/verified-accounts.svg' },
        { id: 4, text: 'Only Accounts You Mention', icon: 'path/to/accounts-mention.svg' },
    ];
    selectedReplyType = this.replyTypes[0];

    @ViewChild('footerComponent') footerComponent!: PostFooterComponent;

    constructor(
        private router: Router,
        private viewportScroller: ViewportScroller,
        private scrollPositionService: ScrollPositionService,
        private userService: UserService,
        private userPostsDataService: UserPostsDataService
    ) {}

    ngOnInit(): void {
        this.profileError = null;
        this.userService.getUserProfile(this.previewAccountHandle).subscribe({
            next: (profile) => {
                this.UserID = profile.UserID;
                this.Username = profile.UserName;
                this.profileLoaded = true;
                this.loadTruthshortsPosts();
            },
            error: () => {
                this.profileError = `Could not load profile @${this.previewAccountHandle}. Ensure the account exists and the API is reachable.`;
            },
        });
    }

    private loadTruthshortsPosts(): void {
        this.postsLoading = true;
        this.postsError = null;
        const postTypes = [1, 2, 3, 5, 20, 21];
        this.userPostsDataService
            .loadUserPostsData(this.UserID, 1, this.pageSize, postTypes, undefined)
            .subscribe({
                next: (posts) => {
                    this.UserPostsData = posts;
                    this.postsLoading = false;
                },
                error: () => {
                    this.postsError = 'Could not load posts for this account.';
                    this.postsLoading = false;
                },
            });
    }

    onApiLogoError(event: Event): void {
        const el = event.target as HTMLImageElement;
        if (el?.src && !el.src.includes('avatar-placeholder.svg')) {
            el.src = this.avatarPlaceholder;
        }
    }

    updatePostContent(postContent: string): void {
        if (postContent && postContent.length > 0) {
            this.hasPostText = true;
        } else {
            this.hasPostText = false;
        }
    }

    updatePostMediaUrls(media: any[]): void {
        this.mediaUrls = media.map((item) => ({ ...item }));
    }

    updateReplyType(type: any): void {
        this.selectedReplyType = type;
    }

    removeMediaFromFooter(index: number): void {
        if (this.footerComponent) {
            this.footerComponent.removeMediaAtIndex(index);
        }
    }

    /** Preview: composer Post does not call the API. */
    savePost(): void {}

    onPostEditedOrDeleted(): void {
        if (this.profileLoaded) {
            this.loadTruthshortsPosts();
        }
    }

    navigateToPost(username: string, postId: number): void {
        const currentPosition = this.viewportScroller.getScrollPosition();
        this.scrollPositionService.setScrollPosition(currentPosition);
        this.router.navigate([`${username}/posts/${postId}`]);
    }
}
